import { ExamInterface } from "../interfaces/examInterfaces";
import { Grade } from "../types/App";
import { db } from "./firebaseConfig";
import { collection, doc, getDoc, setDoc, updateDoc, writeBatch } from "firebase/firestore";

export const COLLECTIONS = {
  AREA_TEMPLATES: "areaTemplates",
  GRADES: "grades",
  AREAS: "areas",
  USERS: "users",
  SCHOOL_CLASSES: "schoolClasses",
  SUBJECTS: "subjects",
  EXAMINED_AREAS: "examinedAreas",
  EXAMS: "exams",
  STUDENTS: "students",
  TOPICS: "topics",
  EXAM_TOPICS: "examTopics",
  EXERCISES: "exercises",
  WORK_PLANS: "workPlans",
};

export const DIRECTORIES = {
  WP_DIRECTORIES: "wpDirectories",
  EXAM_DIRECTORIES: "examDirectories",
};

export type CollectionProps = (typeof COLLECTIONS)[keyof typeof COLLECTIONS];
export type DirectoriesProps = (typeof DIRECTORIES)[keyof typeof DIRECTORIES];

class FirebaseService {
  _getUserRef({ userId }: { userId: string }) {
    return doc(db, COLLECTIONS.USERS + "/" + userId);
  }

  _getTopicsRef = ({ grade }: { grade: Grade }) => {
    return doc(db, COLLECTIONS.EXAM_TOPICS + "/" + grade);
  };

  _getExercisesRef = ({ grade, objectiveId }: { grade: Grade; objectiveId: string }) => {
    return doc(db, COLLECTIONS.EXAM_TOPICS + "/" + grade + "/" + COLLECTIONS.EXERCISES + "/" + objectiveId);
  };

  _getItemRef({ userId, itemId, collectionName }: { userId: string; itemId: string; collectionName: string }) {
    return doc(collection(this._getUserRef({ userId }), collectionName), itemId);
  }

  async updateUserField({ userId, field, data }: { userId: string; field: string; data: any }) {
    if (!userId) throw new Error("userId must be defined.");
    if (!field) throw new Error("field must be defined.");

    try {
      const userDocRef = this._getUserRef({ userId });

      await updateDoc(userDocRef, {
        [field]: data,
      });
    } catch (error) {
      console.error("Error updating user field:", error);
      throw error;
    }
  }

  async fetchItem({
    userId,
    itemId,
    collectionName,
  }: {
    userId: string;
    itemId: string;
    collectionName: CollectionProps;
  }) {
    const itemDoc = await getDoc(this._getItemRef({ userId, itemId, collectionName }));
    if (!itemDoc.exists) {
      throw new Error(`Item ${itemId} does not exist for user ${userId}.`);
    } else {
      return itemDoc.data();
    }
  }

  async insertItem({
    userId,
    pathString,
    item,
    index,
    collectionName,
    directories,
  }: {
    userId: string;
    pathString: string;
    item: ExamInterface;
    index?: number;
    collectionName: CollectionProps;
    directories: DirectoriesProps;
  }): Promise<string> {
    try {
      const userRef = this._getUserRef({ userId });
      const userDoc = await getDoc(userRef);

      const itemId = item.id;
      if (!itemId) {
        throw new Error("The 'item' must have an 'id' property.");
      }

      const docRef = doc(userRef, collectionName, itemId);
      await setDoc(docRef, item);

      let itemDirectories =
        userDoc.data()![directories] ?? ({} as { [key: string]: { grade: Grade; name: string; id: string }[] });
      let items = itemDirectories[pathString];

      if (items) {
        items.splice(index ?? items.length, 0, { grade: item.grade, name: item.name, id: docRef.id });
      } else {
        items = [{ grade: item.grade, name: item.name, id: docRef.id }];
      }
      itemDirectories[pathString] = items;
      await updateDoc(userRef, { [directories]: itemDirectories });
      return itemId;
    } catch (error) {
      console.error("Error inserting item:", error);
      throw error;
    }
  }

  async insertFolder({
    userId,
    pathString,
    name,
    index,
    directories,
  }: {
    userId: string;
    pathString: string;
    name: string;
    index?: number;
    directories: DirectoriesProps;
  }) {
    try {
      const userRef = this._getUserRef({ userId });
      const userDoc = await getDoc(userRef);

      let itemDirectories =
        userDoc.data()![directories] ?? ({} as { [key: string]: { isDirectory: true; name: string; id: string }[] });
      let parentItem = itemDirectories[pathString];

      // Add folder-name to parent-array
      if (parentItem) {
        parentItem.splice(index ?? parentItem.length, 0, {
          name,
          id: new Date().getTime().toString(),
          isDirectory: true,
        });
      } else {
        parentItem = [{ name, isDirectory: true, id: new Date().getTime().toString() }];
      }
      itemDirectories[pathString] = parentItem;

      // Add new path to wpDirectories
      pathString += (pathString === "/" ? "" : "/") + name;
      if (itemDirectories) {
        itemDirectories[pathString] = [];
      } else {
        itemDirectories = { [pathString]: [] };
      }
      await updateDoc(userRef, { [directories]: itemDirectories });
    } catch (error) {
      console.error("Error inserting folder:", error);
      throw error;
    }
  }

  async updateItemSubcolletion({
    userId,
    item,
    collectionName,
  }: {
    userId: string;
    item: ExamInterface;
    collectionName: CollectionProps;
  }) {
    try {
      const userRef = this._getUserRef({ userId });

      const itemId = item.id;
      if (!itemId) {
        throw new Error("The 'item' must have an 'id' property.");
      }

      const docRef = doc(userRef, collectionName, itemId);
      await setDoc(docRef, item);
    } catch (error) {
      console.error("Error inserting item:", error);
      throw error;
    }
  }

  async renameItem({
    userId,
    pathString,
    itemId,
    name,
    collectionName,
    directories,
  }: {
    userId: string;
    pathString: string;
    itemId: string;
    name: string;
    collectionName: CollectionProps;
    directories: DirectoriesProps;
  }) {
    try {
      const userRef = this._getUserRef({ userId });
      const userDoc = await getDoc(userRef);
      const itemRef = this._getItemRef({ userId, itemId, collectionName });

      let itemDirectories = userDoc.data()![directories] as { [key: string]: { id: string }[] };
      let items = itemDirectories[pathString].map((currentItem) =>
        currentItem.id === itemId ? { ...currentItem, name } : currentItem
      );
      itemDirectories[pathString] = items;
      const batch = writeBatch(db);
      batch.update(userRef, { [directories]: itemDirectories });
      batch.update(itemRef, { name });
      return batch.commit();
    } catch (error) {
      console.error("Error renaming item:", error);
      throw error;
    }
  }

  async renameFolder({
    userId,
    pathString,
    folderId,
    prevName,
    newName,
    directories,
  }: {
    userId: string;
    pathString: string;
    folderId: string;
    prevName: string;
    newName: string;
    directories: DirectoriesProps;
  }) {
    try {
      const userRef = this._getUserRef({ userId });
      const userDoc = await getDoc(userRef);
      let prevItemDirectories = userDoc.data()![directories] as {
        [key: string]: { isDirectory: true; name: string; id: string }[];
      };

      // Change name in parent-array
      let items = prevItemDirectories[pathString].map((currentItem) =>
        currentItem.id === folderId ? { ...currentItem, name: newName } : currentItem
      );
      prevItemDirectories[pathString] = items;

      // Rename all common prefix paths
      pathString += pathString === "/" ? "" : "/";
      const prevPrefixPath = pathString + prevName;
      const newPrefixPath = pathString + newName;
      let itemDirectories: { [key: string]: { isDirectory: true; name: string; id: string }[] } = {};
      for (const key in prevItemDirectories) {
        let newKey =
          key === prevPrefixPath || key.startsWith(prevPrefixPath + "/")
            ? key.replace(prevPrefixPath, newPrefixPath)
            : key;
        itemDirectories[newKey] = prevItemDirectories[key];
      }

      // Update
      await updateDoc(userRef, { [directories]: itemDirectories });
    } catch (error) {
      console.error("Error renaming folder:", error);
      throw error;
    }
  }

  async moveItem({
    userId,
    pathString,
    newPathString,
    itemId,
    directories,
  }: {
    userId: string;
    pathString: string;
    newPathString: string;
    itemId: string;
    directories: DirectoriesProps;
  }) {
    const userRef = this._getUserRef({ userId });
    const userDoc = await getDoc(userRef);

    let itemDirectories = userDoc.data()![directories] as {
      [key: string]: { grade: 3 | 4 | 5 | 6; name: string; id: string }[];
    };
    let prevItems = [];
    let newItems = itemDirectories[newPathString];

    // Remove item from previous array and append it to the new one.
    for (let item of Object.values(itemDirectories[pathString])) {
      if (item.id === itemId) {
        newItems.push(item);
      } else {
        prevItems.push(item);
      }
    }
    itemDirectories[pathString] = prevItems;
    itemDirectories[newPathString] = newItems;

    // Write / Update
    await updateDoc(userRef, { [directories]: itemDirectories });
  }

  async moveFolder({
    userId,
    pathString,
    newPathString,
    folderId,
    directories,
  }: {
    userId: string;
    pathString: string;
    newPathString: string;
    folderId: string;
    directories: DirectoriesProps;
  }) {
    const userRef = this._getUserRef({ userId });
    const userDoc = await getDoc(userRef);
    const prevItemDirectories = userDoc.data()![directories] as {
      [key: string]: { isDirectory: true; name: string; id: string }[];
    };

    // Delete from previous parent folder
    const prevItems: { isDirectory: true; name: string; id: string }[] = [];
    let docToMove: { isDirectory: true; name: string; id: string } | undefined = undefined;
    for (const f of prevItemDirectories[pathString]) {
      if (f.id === folderId) {
        docToMove = f;
      } else {
        prevItems.push(f);
      }
    }
    prevItemDirectories[pathString] = prevItems;

    // Rename all common prefix paths
    pathString += pathString === "/" ? "" : "/";
    const prevPrefixPath = pathString + docToMove!.name;
    const newPrefixPath = newPathString + (newPathString === "/" ? "" : "/") + docToMove!.name;
    let itemDirectories: { [key: string]: { isDirectory: true; name: string; id: string }[] } = {};
    for (const key of Object.keys(prevItemDirectories)) {
      let newKey =
        key === prevPrefixPath || key.startsWith(prevPrefixPath + "/")
          ? key.replace(prevPrefixPath, newPrefixPath)
          : key;
      itemDirectories[newKey] = prevItemDirectories[key];
    }

    // Add folder-doc to new parent-array
    itemDirectories[newPathString].push(docToMove!);

    // Update
    await updateDoc(userRef, { [directories]: itemDirectories });
  }

  async moveAndRenameItem({
    userId,
    pathString,
    newPathString,
    itemId,
    directories,
    newName,
  }: {
    userId: string;
    pathString: string;
    newPathString: string;
    itemId: string;
    directories: DirectoriesProps;
    newName: string;
  }) {
    const userRef = this._getUserRef({ userId });
    const userDoc = await getDoc(userRef);

    let itemDirectories = userDoc.data()![directories] as {
      [key: string]: { grade: 3 | 4 | 5 | 6; name: string; id: string }[];
    };
    let prevItems = [];
    let newItems = itemDirectories[newPathString];

    // Remove item from previous array and append it to the new one.
    for (let item of Object.values(itemDirectories[pathString])) {
      if (item.id === itemId) {
        item.name = newName;
        newItems.push(item);
      } else {
        prevItems.push(item);
      }
    }
    itemDirectories[pathString] = prevItems;
    itemDirectories[newPathString] = newItems;

    // Write / Update
    await updateDoc(userRef, { [directories]: itemDirectories });
  }

  async moveAndRenameFolder({
    userId,
    pathString,
    newPathString,
    folderId,
    directories,
    newName,
  }: {
    userId: string;
    pathString: string;
    newPathString: string;
    folderId: string;
    directories: DirectoriesProps;
    newName: string;
  }) {
    const userRef = this._getUserRef({ userId });
    const userDoc = await getDoc(userRef);
    const prevItemDirectories = userDoc.data()![directories] as {
      [key: string]: { isDirectory: true; name: string; id: string }[];
    };

    // Delete from previous parent folder
    const prevItems: { isDirectory: true; name: string; id: string }[] = [];
    let docToMove: { isDirectory: true; name: string; id: string } | undefined = undefined;
    for (const f of prevItemDirectories[pathString]) {
      if (f.id === folderId) {
        docToMove = f;
      } else {
        prevItems.push(f);
      }
    }
    prevItemDirectories[pathString] = prevItems;

    // Rename all common prefix paths
    pathString += pathString === "/" ? "" : "/";
    const prevPrefixPath = pathString + docToMove!.name;
    docToMove!.name = newName;
    const newPrefixPath = newPathString + (newPathString === "/" ? "" : "/") + docToMove!.name;
    let itemDirectories: { [key: string]: { isDirectory: true; name: string; id: string }[] } = {};
    for (const key of Object.keys(prevItemDirectories)) {
      let newKey =
        key === prevPrefixPath || key.startsWith(prevPrefixPath + "/")
          ? key.replace(prevPrefixPath, newPrefixPath)
          : key;
      itemDirectories[newKey] = prevItemDirectories[key];
    }

    // Add folder-doc to new parent-array
    itemDirectories[newPathString].push(docToMove!);

    // Update
    await updateDoc(userRef, { [directories]: itemDirectories });
  }

  async deleteItem({
    userId,
    pathString,
    itemId,
    collectionName,
    directories,
  }: {
    userId: string;
    pathString: string;
    itemId: string;
    collectionName: CollectionProps;
    directories: DirectoriesProps;
  }) {
    try {
      const userRef = this._getUserRef({ userId });
      const userDoc = await getDoc(userRef);
      const itemRef = this._getItemRef({ userId, itemId, collectionName });

      let itemDirectories = userDoc.data()![directories] as { [key: string]: { id: string }[] };
      itemDirectories[pathString] = itemDirectories[pathString].filter((item) => item.id !== itemId);

      const batch = writeBatch(db);
      batch.update(userRef, { [directories]: itemDirectories });
      batch.delete(itemRef);
      return await batch.commit();
    } catch (error) {
      console.error("Error deleting item:", error);
    }
  }

  async deleteFolder({
    userId,
    pathString,
    folderId,
    name,
    directories,
  }: {
    userId: string;
    pathString: string;
    folderId: string;
    name: string;
    directories: DirectoriesProps;
  }) {
    try {
      const userRef = this._getUserRef({ userId });
      const userDoc = await getDoc(userRef);
      let prevItemDirectories = userDoc.data()![directories] as {
        [key: string]: { isDirectory: true; name: string; id: string }[];
      };
      let totalPath = pathString + (pathString === "/" ? "" : "/") + name;
      // Delete from parent id
      prevItemDirectories[pathString] = prevItemDirectories[pathString].filter((f) => f.id !== folderId);

      // Delete routes
      let itemDirectories: { [key: string]: any } = {};
      for (const key in prevItemDirectories) {
        if (!(key === totalPath || key.startsWith(totalPath + "/"))) {
          itemDirectories[key] = prevItemDirectories[key];
        }
      }

      let triggerCleanupOrphanedWorkPlans = false;
      let triggerCleanupOrphanedExams = false;
      // Write / Update
      if (directories === DIRECTORIES.WP_DIRECTORIES) {
        triggerCleanupOrphanedWorkPlans = prevItemDirectories[totalPath] && prevItemDirectories[totalPath].length > 0;
      }
      if (directories === DIRECTORIES.EXAM_DIRECTORIES) {
        triggerCleanupOrphanedExams = prevItemDirectories[totalPath] && prevItemDirectories[totalPath].length > 0;
      }

      await updateDoc(userRef, {
        [directories]: itemDirectories,
        triggerCleanupOrphanedWorkPlans,
        triggerCleanupOrphanedExams,
      });
    } catch (error) {
      console.error("Error deleting folder:", error);
    }
  }
}

export default new FirebaseService();
